import { layOutOptions, mulberry32, seedFrom, shuffle } from "./random.js";
/**
 * Which pieces make this? — the part/whole game.
 *
 * A thing built out of coloured blocks sits above: a house, a boat, a car. Below are sets of loose
 * pieces, and the child picks the set the thing is made of. It is the counting-free half of
 * composition and nothing else in the bundle asks for it — every other activity compares a shape
 * with a shape, and this one asks a child to take a shape APART in their head and check the
 * inventory against a pile.
 *
 * **The pile is what makes it hard, and it is why the pieces are never shown in place.** An option
 * that laid its pieces out the way the figure does would be answerable by overlaying two pictures.
 * Scattered, the only route is to decompose the whole and then count kinds and colours, which is
 * the thing being taught.
 *
 * **Distractors are near misses in three flavours, and they are the ladder.** One piece the wrong
 * KIND is visible at a glance; one piece the wrong COLOUR needs the inventory checked twice; and a
 * SWAP — two pieces exchanging colours — leaves the pile holding exactly the right kinds and
 * exactly the right colours, wrongly paired. That last one is the same idea as the swap levels in
 * spot-the-difference, and it is the subtlest wrong answer this game can offer.
 */
/**
 * A piece is a SHAPE, a COLOUR and a SIZE, and all three are part of what it is.
 *
 * **Size being in the identity is the fix for the fault that made the first version unplayable.**
 * That version let a template give each piece any width and height it liked, and the piles below
 * had no way to know them — so a pile drew every piece at a house size of its own. A mushroom's cap
 * was a wide shallow dome in the figure and a tall narrow one in the pile, its stem a thin stick
 * above and a fat slab below. The child was being asked to match pieces that were not the same
 * pieces. Nothing else about the game mattered while that was true.
 *
 * So a shape now has ONE aspect ratio wherever it appears, and a template chooses from three sizes
 * rather than from a continuum. A piece therefore looks identical in the figure and in the pile,
 * scaled but never restretched — which is the only version of "which pieces make this" that can be
 * answered by looking.
 *
 * Seven shapes, four colours, three sizes: 84 distinct pieces.
 */
export const PIECE_KINDS = 7;
export const PIECE_COLORS = 4;
export const PIECE_SIZES = 3;
export const packPart = (kind, color, size) => (kind * PIECE_COLORS + color) * PIECE_SIZES + size;
export const partKind = (part) => Math.floor(part / (PIECE_COLORS * PIECE_SIZES));
export const partColor = (part) => Math.floor(part / PIECE_SIZES) % PIECE_COLORS;
export const partSize = (part) => part % PIECE_SIZES;
/** Shape ids, for readable templates below. The renderer draws these and only these. */
const CIRCLE = 0;
const DOME = 1;
const TRIANGLE = 2;
const SQUARE = 3;
const BAR = 4;
const STICK = 5;
const TRAPEZOID = 6;
/** Colour ids. Which hue each is, is the renderer's business. */
const YELLOW = 0;
const BLUE = 1;
const RED = 2;
const GREEN = 3;
/** Size ids. */
const SMALL = 0;
const MEDIUM = 1;
const LARGE = 2;
/**
 * The shape of each kind at size 1, as width and height in figure units.
 *
 * Here rather than in the renderer because it is what makes a piece the same piece in both views:
 * the pile has to draw a bar as a bar, and it only knows the token. Two tables would be two
 * opinions about what a bar is, and the game would break exactly the way it broke before.
 *
 * The shapes earn their places by being tellable apart in a pile at about 40 px: a BAR lies down, a
 * STICK stands up, a DOME is a wide shallow hump, a TRAPEZOID has slanted sides.
 */
export const PIECE_ASPECT = [
    { w: 1.0, h: 1.0 }, // circle
    { w: 1.4, h: 0.86 }, // dome
    { w: 1.05, h: 1.0 }, // triangle
    { w: 0.95, h: 0.95 }, // square
    { w: 1.7, h: 0.55 }, // bar
    { w: 0.26, h: 1.1 }, // stick
    { w: 1.5, h: 0.6 }, // trapezoid
];
/** The three sizes, as a multiplier on the aspect above. */
export const PIECE_SCALE = [0.55, 0.8, 1.1];
/** A piece's width and height in figure units, which is all either view needs. */
export function partBox(part) {
    const aspect = PIECE_ASPECT[partKind(part)] ?? { w: 1, h: 1 };
    const scale = PIECE_SCALE[partSize(part)] ?? 1;
    return { w: aspect.w * scale, h: aspect.h * scale };
}
/**
 * The figures, grouped by how many pieces they take.
 *
 * **Every template's piece MULTISET is unique across the whole table**, and that is load-bearing
 * rather than tidy: the stimulus travels to the renderer as a bare list of pieces, so the assembled
 * picture has to be recoverable from that list alone. Two figures made of the same pieces would
 * make the target card ambiguous. A test asserts it.
 *
 * Pieces overlap where a real construction toy would overlap — a rocket's window sits on its body.
 * That is drawing order, not extra pieces, and the inventory is still exactly what the list says.
 */
export const PARTS_TEMPLATES = [
    // Two pieces.
    {
        name: "tree",
        pieces: [
            { part: packPart(STICK, YELLOW, SMALL), x: 0, y: 0.58 },
            { part: packPart(CIRCLE, GREEN, LARGE), x: 0, y: -0.28 },
        ],
    },
    {
        name: "house",
        pieces: [
            { part: packPart(SQUARE, BLUE, LARGE), x: 0, y: 0.36 },
            { part: packPart(TRIANGLE, RED, LARGE), x: 0, y: -0.4 },
        ],
    },
    {
        name: "mushroom",
        pieces: [
            { part: packPart(STICK, YELLOW, MEDIUM), x: 0, y: 0.44 },
            { part: packPart(DOME, RED, LARGE), x: 0, y: -0.16 },
        ],
    },
    {
        name: "ice-cream",
        pieces: [
            { part: packPart(TRIANGLE, YELLOW, LARGE), x: 0, y: 0.42, flip: true },
            { part: packPart(CIRCLE, RED, MEDIUM), x: 0, y: -0.42 },
        ],
    },
    // The same two shapes as the tree, in different colours: a pile has to be read, not glanced at.
    {
        name: "balloon",
        pieces: [
            { part: packPart(STICK, YELLOW, SMALL), x: 0, y: 0.6 },
            { part: packPart(CIRCLE, BLUE, LARGE), x: 0, y: -0.3 },
        ],
    },
    // Three pieces.
    /**
     * The hull is a FLIPPED trapezoid — wide edge up.
     *
     * Drawn the other way round it is a pyramid with a stick in it, which is what the first version
     * of this figure was. The mast is deliberately taller than the sail so it shows above it, because
     * a mast that stops at the sail reads as a coloured triangle sitting on a boat.
     */
    {
        name: "boat",
        pieces: [
            { part: packPart(STICK, YELLOW, LARGE), x: 0, y: -0.14 },
            { part: packPart(TRIANGLE, RED, MEDIUM), x: 0.28, y: -0.18 },
            { part: packPart(TRAPEZOID, BLUE, LARGE), x: 0, y: 0.56, flip: true },
        ],
    },
    /**
     * Two scoops on a cone, and it replaced a chick.
     *
     * The chick was a dome body, a circle head and a triangle beak, and it did not survive being
     * drawn: with no rotation available the beak could only point up or down, so it sat beside the
     * head like a dropped hat and the whole figure read as a blob. Every piece here is stacked on the
     * one below it, which is what makes a figure legible when nothing can be turned.
     */
    {
        name: "double-cone",
        pieces: [
            { part: packPart(TRIANGLE, YELLOW, LARGE), x: 0, y: 0.44, flip: true },
            { part: packPart(CIRCLE, RED, MEDIUM), x: 0, y: -0.16 },
            { part: packPart(CIRCLE, GREEN, SMALL), x: 0, y: -0.62 },
        ],
    },
    {
        name: "cart",
        pieces: [
            { part: packPart(BAR, RED, LARGE), x: 0, y: -0.08 },
            { part: packPart(CIRCLE, BLUE, SMALL), x: -0.45, y: 0.4 },
            { part: packPart(CIRCLE, BLUE, SMALL), x: 0.45, y: 0.4 },
        ],
    },
    {
        name: "tower",
        pieces: [
            { part: packPart(SQUARE, GREEN, LARGE), x: 0, y: 0.5 },
            { part: packPart(SQUARE, BLUE, MEDIUM), x: 0, y: -0.14 },
            { part: packPart(TRIANGLE, YELLOW, MEDIUM), x: 0, y: -0.66 },
        ],
    },
    // Four pieces.
    {
        name: "car",
        pieces: [
            { part: packPart(TRAPEZOID, BLUE, MEDIUM), x: 0, y: -0.4 },
            { part: packPart(BAR, YELLOW, LARGE), x: 0, y: 0.06 },
            { part: packPart(CIRCLE, GREEN, SMALL), x: -0.5, y: 0.44 },
            { part: packPart(CIRCLE, GREEN, SMALL), x: 0.5, y: 0.44 },
        ],
    },
    {
        name: "train",
        pieces: [
            { part: packPart(SQUARE, YELLOW, MEDIUM), x: -0.5, y: -0.48 },
            { part: packPart(BAR, RED, LARGE), x: 0, y: 0.04 },
            { part: packPart(CIRCLE, BLUE, SMALL), x: -0.5, y: 0.44 },
            { part: packPart(CIRCLE, BLUE, SMALL), x: 0.5, y: 0.44 },
        ],
    },
    /**
     * The fins are a trapezoid straddling the body's foot, not a dome hanging below it.
     *
     * The first version put a flipped dome under the rocket, and because a dome is drawn from its
     * flat edge it came out detached — a green smile floating below a blue box. A trapezoid overlaps
     * the body by design and reads as fins from either side.
     */
    {
        name: "rocket",
        pieces: [
            { part: packPart(TRIANGLE, RED, MEDIUM), x: 0, y: -0.66 },
            { part: packPart(SQUARE, BLUE, LARGE), x: 0, y: 0.08 },
            { part: packPart(CIRCLE, YELLOW, SMALL), x: 0, y: -0.04 },
            { part: packPart(TRAPEZOID, GREEN, MEDIUM), x: 0, y: 0.66 },
        ],
    },
    /**
     * A robot, which replaced a crane.
     *
     * The crane's fourth piece was a hook, and a hook hangs from a cable this vocabulary has no way
     * to draw — so it floated in the middle of the card attached to nothing. Every piece of the robot
     * touches another one, which is the rule these figures have to obey when no piece can be rotated
     * and nothing can be joined by a line.
     */
    {
        name: "robot",
        pieces: [
            { part: packPart(SQUARE, BLUE, LARGE), x: 0, y: -0.44 },
            { part: packPart(CIRCLE, RED, SMALL), x: -0.24, y: -0.48 },
            { part: packPart(CIRCLE, RED, SMALL), x: 0.24, y: -0.48 },
            { part: packPart(BAR, YELLOW, LARGE), x: 0, y: 0.42 },
        ],
    },
];
/** Canonical form of a pile: the pieces sorted, so two piles compare regardless of order. */
export const canonParts = (parts) => [...parts].sort((a, b) => a - b).join(",");
const BY_PIECES = new Map(PARTS_TEMPLATES.map((t) => [canonParts(t.pieces.map((p) => p.part)), t]));
/**
 * The figure a pile of pieces builds, or undefined for a pile that builds nothing.
 *
 * This is how the assembled picture reaches the renderer: the stimulus carries a bare list of
 * pieces, and the target is whichever figure is made of exactly those. Undefined for every wrong
 * answer, which is correct — a wrong pile is only ever drawn as a pile.
 */
export const templateForParts = (parts) => BY_PIECES.get(canonParts(parts));
/** Templates taking exactly `count` pieces. */
const templatesWith = (count) => PARTS_TEMPLATES.filter((t) => t.pieces.length === count);
export const PARTS_MIN_DIFFICULTY = 1;
export const PARTS_MAX_DIFFICULTY = 8;
/**
 * Eight levels: bigger figures, then subtler wrong piles.
 *
 * The two axes alternate on purpose. Going straight up the sizes would make level 8 a five-piece
 * figure with an obviously wrong pile beside it, which is more to look at rather than more to work
 * out; going straight up the flaws would ask a four-year-old to spot a colour swap in a two-piece
 * house. Four pieces is the ceiling because an option card on a phone is about 100 px, and five
 * pieces scattered inside it stop being separately readable.
 */
function recipeFor(difficulty) {
    switch (difficulty) {
        case 1:
            return { size: 2, optionCount: 3, flaws: ["kind", "kind"] };
        case 2:
            return { size: 2, optionCount: 4, flaws: ["kind", "kind", "color"] };
        case 3:
            return { size: 3, optionCount: 4, flaws: ["kind", "kind", "color"] };
        // Colour only: every pile holds the right shapes, so the silhouette of the pile says nothing.
        case 4:
            return { size: 3, optionCount: 4, flaws: ["color", "color", "color"] };
        case 5:
            return { size: 4, optionCount: 4, flaws: ["kind", "color", "color"] };
        case 6:
            return { size: 4, optionCount: 4, flaws: ["color", "color", "color"] };
        // A swap: the right kinds and the right colours, wrongly paired.
        case 7:
            return { size: 4, optionCount: 4, flaws: ["swap", "color", "color"] };
        default:
            return { size: 4, optionCount: 4, flaws: ["swap", "swap", "color"] };
    }
}
export function generateParts(task, formId, trialIndex, difficulty) {
    if (difficulty < PARTS_MIN_DIFFICULTY || difficulty > PARTS_MAX_DIFFICULTY) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const { size, optionCount, flaws } = recipeFor(difficulty);
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    const pool = templatesWith(size);
    const figure = pool[Math.floor(rand() * pool.length)];
    if (!figure)
        throw new Error(`no ${size}-piece figure for ${task.id} at difficulty ${difficulty}`);
    const answer = [...figure.pieces.map((p) => p.part)].sort((a, b) => a - b);
    const seen = new Set([canonParts(answer)]);
    const distractors = [];
    /**
     * Each distractor is drawn until it is genuinely new.
     *
     * Bounded, and running out throws rather than shipping the row short: a trial with a duplicated
     * option has two correct-looking answers, and a trial with fewer cards than its level claims is a
     * level whose label has stopped meaning anything.
     */
    for (const flaw of flaws.slice(0, optionCount - 1)) {
        let made = null;
        for (let attempt = 0; attempt < 200 && !made; attempt++) {
            const candidate = flawed(answer, flaw, rand);
            if (!candidate)
                continue;
            const key = canonParts(candidate);
            if (seen.has(key))
                continue;
            seen.add(key);
            made = candidate;
        }
        if (!made) {
            throw new Error(`could not build a distinct "${flaw}" pile for ${figure.name} at difficulty ${difficulty}`);
        }
        distractors.push(made);
    }
    const { options, correctIndex } = layOutOptions(answer, distractors, rand);
    return { target: answer, options, correctIndex };
}
/**
 * One wrong pile, or null when this pile cannot carry this flaw.
 *
 * Null rather than a substitute: a swap needs two pieces of different colours, and a figure whose
 * pieces are all one colour has no swap to make. Returning something else would quietly hand the
 * child an easier distractor than the level promised.
 */
function flawed(answer, flaw, rand) {
    const parts = [...answer];
    if (flaw === "swap") {
        const pairs = [];
        for (let i = 0; i < parts.length; i++) {
            for (let j = i + 1; j < parts.length; j++) {
                const a = parts[i];
                const b = parts[j];
                // Different colours, or the swap changes nothing; different kinds, or swapping the colours
                // of two same-kind pieces produces the identical multiset back.
                if (partColor(a) !== partColor(b) && partKind(a) !== partKind(b))
                    pairs.push([i, j]);
            }
        }
        const pick = pairs[Math.floor(rand() * pairs.length)];
        if (!pick)
            return null;
        const [i, j] = pick;
        const a = parts[i];
        const b = parts[j];
        // Only the colours change hands. Each piece keeps its own shape and its own size, which is what
        // leaves the pile holding exactly the right inventory of both.
        parts[i] = packPart(partKind(a), partColor(b), partSize(a));
        parts[j] = packPart(partKind(b), partColor(a), partSize(b));
        return parts.sort((x, y) => x - y);
    }
    const at = Math.floor(rand() * parts.length);
    const piece = parts[at];
    // Size is never the flaw. It is part of a piece's identity, so a "same shape, one size up" pile
    // would be a legitimate wrong answer — and an unfair one, because two sizes of the same shape in
    // two different piles is a comparison of remembered magnitudes rather than of inventories.
    if (flaw === "kind") {
        const kinds = shuffle(Array.from({ length: PIECE_KINDS }, (_, k) => k).filter((k) => k !== partKind(piece)), rand);
        parts[at] = packPart(kinds[0], partColor(piece), partSize(piece));
    }
    else {
        const colors = shuffle(Array.from({ length: PIECE_COLORS }, (_, c) => c).filter((c) => c !== partColor(piece)), rand);
        parts[at] = packPart(partKind(piece), colors[0], partSize(piece));
    }
    return parts.sort((x, y) => x - y);
}
/** Matching scoring: the chosen pile must be the one the figure is made of. */
export function scorePartsTrial(correctIndex, chosenIndex) {
    return correctIndex === chosenIndex;
}
//# sourceMappingURL=parts.js.map